import React, { useState, useEffect, createContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Users from '../schemas/public/Users';
import { UserDataProp } from '../models/userDataProp';
import { Loader } from '../components/Loader';
import { getUser } from '../stores/User';
import {
    exitActAsTokenOverlay,
    getAccessToken,
    getRefreshToken,
    isActAsSessionActive
} from '../utilities/storage/tokenStorage';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { runStorageLogout } from '../utilities/storage/storageDomains';
import { clearActAsMarker, getActAsMarker } from '../utilities/storage/actAsMarkerStorage';
import { useActAsMode } from './hooks/useActAsMode';
import { signOutXimiSso } from '../stores/ximi/ximiStimulinSignOut';
import { isXimiUser } from '../utilities/ximi/isXimiUser';
import { useAppSession } from '../hooks/useAppSession';
import useRouteActivity from '../hooks/useRouteActivity';
import { API_URL } from '../config';
import {
    XIMI_LOGIN_MANUAL_ENTRY_PATH,
    XIMI_LOGIN_SELECT_ACCOUNT_PATH,
    XIMI_SSO_CALLBACK_PATH
} from '../routePaths';

export const UserContext = createContext<UserDataProp>({
    user: null,
    setUser: () => {},
    mergeUserData: () => {},
    refreshUser: async (tokenOverride?: string) => null,
    token: '',
    logout: () => {},
    enterActAs: async () => false,
    exitActAs: async () => {},
    isActAsActive: false
});

interface UserProviderProps {
    children?: React.ReactNode;
}

export function UserProvider({ children }: UserProviderProps): JSX.Element {
    const [loaded, setLoaded] = useState<boolean | false>(false);
    const { t } = useTranslation();
    const history = useHistory();
    const { pathname } = useLocation();

    const [propData, setPropData] = useState<UserDataProp>({
        user: null,
        setUser: (userData: Users) => {
            setPropData((prev) => ({ ...prev, user: userData }));
        },
        mergeUserData: (updatedFields: Partial<Users>) => {
            setPropData((prev) => ({
                ...prev,
                user: prev.user ? { ...prev.user, ...updatedFields } : (updatedFields as Users)
            }));
        },
        token: '',
        refreshUser: async () => null,
        logout: () => {}
    });

    const { startSession: startAppSession, endSession: endAppSession } = useAppSession(
        propData.token
    );

    // Track time spent on important routes/areas (games / album)
    useRouteActivity(propData.token);

    const logout = useCallback(async () => {
        // Une déconnexion est toujours celle du compte porteur réel (le Pro) : on sort
        // d'abord de l'overlay act-as pour révoquer SA session — les sessions accompagnées
        // chaînées meurent d'elles-mêmes au renouvellement suivant (PARENT_REVOKED).
        exitActAsTokenOverlay();
        const refreshToken = await getRefreshToken();
        if (refreshToken) {
            axios.post(`${API_URL}/auth/logout`, { refreshToken }).catch(() => {});
        }
        // end_session AVANT le nettoyage : il lit le user oidc (id_token_hint).
        await signOutXimiSso().catch(() => {}); // no-op si user classique
        await runStorageLogout();
        setPropData((prev) => ({ ...prev, token: '', user: null }));
        history.replace('/login');
    }, [history]);

    const refreshUser = useCallback(
        async (tokenOverride?: string): Promise<Users | null> => {
            // Use provided token or get the most current token from localStorage
            const currentToken = tokenOverride || (await getAccessToken()) || '';
            if (!currentToken) {
                console.warn('No token available for refreshUser');
                return null;
            }
            try {
                const updatedUser: Users | null = await getUser();
                if (updatedUser) {
                    setPropData((prev) => ({ ...prev, user: updatedUser, token: currentToken }));
                    return updatedUser;
                } else {
                    // If a token is found but no user
                    // Do not update state if tokenOverride is provided (login case)
                    // This allows the login function to handle the error itself
                    if (!tokenOverride) {
                        // If this is an automatic refresh (no tokenOverride), log out
                        logout();
                    } else {
                        // If this is a manual login, just set user to null to allow verification
                        setPropData((prev) => ({ ...prev, user: null, token: currentToken }));
                    }
                    return null;
                }
            } catch (error) {
                console.error('Error refreshing user:', error);
                // In case of error, do not log out if it is a manual login.
                if (!tokenOverride) {
                    logout();
                } else {
                    setPropData((prev) => ({ ...prev, user: null }));
                }
                return null;
            }
        },
        [logout]
    );

    const setUser = useCallback((userData: Users) => {
        setPropData((prev) => ({ ...prev, user: userData }));
    }, []);

    const mergeUserData = useCallback((updatedFields: Partial<Users>) => {
        setPropData((prev) => ({
            ...prev,
            user: prev.user ? { ...prev.user, ...updatedFields } : (updatedFields as Users)
        }));
    }, []);

    const { enterActAs, exitActAs } = useActAsMode({ refreshUser, endAppSession, startAppSession });

    // Marqueur présent + overlay mémoire vide = l'app a redémarré pendant un accompagnement :
    // le Pro est revenu sur son compte, on le lui annonce (décision 6 du plan).
    useEffect(() => {
        const announceEndedActAs = async () => {
            if (isActAsSessionActive()) return;
            const marker = await getActAsMarker();
            if (!marker) return;
            await clearActAsMarker();
            toast.info(
                t('Vous êtes revenu sur votre espace. La session avec {{name}} est terminée.', {
                    name: marker.beneficiaryFirstName
                })
            );
        };
        void announceEndedActAs();
    }, [t]);

    // Update the propData with the latest functions
    useEffect(() => {
        setPropData((prev) => ({
            ...prev,
            refreshUser,
            logout,
            setUser,
            mergeUserData,
            enterActAs,
            exitActAs
        }));
    }, [refreshUser, logout, setUser, mergeUserData, enterActAs, exitActAs]);

    useEffect(() => {
        const init = async () => {
            const localToken = (await getAccessToken()) || '';

            if (!localToken) {
                if (
                    (pathname !== '/register' &&
                        pathname !== '/new-account' &&
                        pathname !== '/new-account-confirm' &&
                        pathname !== '/forgot-password' &&
                        pathname !== '/guide-utilisateurs' &&
                        pathname !== '/login' &&
                        pathname !== XIMI_SSO_CALLBACK_PATH &&
                        pathname !== XIMI_LOGIN_SELECT_ACCOUNT_PATH &&
                        pathname !== XIMI_LOGIN_MANUAL_ENTRY_PATH &&
                        pathname.slice(0, 7) !== '/login/') ||
                    pathname === '/login/me'
                ) {
                    history.replace('/login');
                    setLoaded(true);
                } else {
                    setLoaded(true);
                }
                return;
            }

            // If the token has changed or if we don't have a user yet
            if (localToken !== propData.token || !propData.user) {
                const updatedUser: Users | null = await getUser();
                if (!updatedUser && localToken !== '') {
                    logout();
                    return;
                }
                setPropData((prev) => ({
                    ...prev,
                    token: localToken,
                    user: updatedUser
                }));
                setLoaded(true);
                // User Ximi : identité gérée dans Ximi → pas de complétion de profil forcée.
                if (updatedUser && !isXimiUser(updatedUser)) {
                    if (
                        !updatedUser.firstName ||
                        !updatedUser.lastName ||
                        updatedUser.firstName === '' ||
                        updatedUser.lastName === ''
                    ) {
                        history.replace('/profile');
                    }
                }
            } else {
                setLoaded(true);
            }
        };
        init();
    }, [pathname, logout]);

    return loaded ? (
        <UserContext.Provider value={{ ...propData, isActAsActive: isActAsSessionActive() }}>
            {children}
        </UserContext.Provider>
    ) : (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100vw'
            }}>
            <Loader disableBg />
        </div>
    );
}
